import { useEffect, useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    LinearProgress,
    Button,
    Chip,
    Divider,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material'
import {
    Upgrade,
    CheckCircle,
    OpenInNew,
    Cancel,
    SmartToy,
    TrendingUp,
    CalendarMonth,
    Payment,
} from '@mui/icons-material'
import {
    EVENT_TIERS,
    AI_PRICING,
    isBillablePlan,
    resolvePlan,
    calculateEventCost,
    dashboardUrl,
    DASHBOARD_LINKS,
    formatCurrency,
    formatNumber,
    tierPricePerK,
    type TenantPlan,
    type SupportedCurrency,
} from '@yourownmetric/shared'

import {
    type SubscriptionData,
    type UsageData,
    useCreateCheckoutMutation,
    useCancelSubscriptionMutation,
    useToggleAiAddonMutation,
    useCreatePortalSessionMutation,
    useVerifyPaymentMutation,
} from '@/apis/billingApi'

// ── Reusable usage meter ──────────────────────────────────────

function UsageMeter({
    label,
    used,
    limit,
    subText,
}: {
    label: string
    used: number
    limit: number
    subText?: string
}) {
    const pct = limit > 0 && isFinite(limit) ? Math.min((used / limit) * 100, 100) : 0
    const color = pct > 90 ? 'error' : pct > 70 ? 'warning' : 'primary'

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                    {label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {formatNumber(used)} / {!isFinite(limit) ? '∞' : formatNumber(limit)}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                color={color}
                sx={{ height: 8, borderRadius: 4 }}
            />
            {subText && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {subText}
                </Typography>
            )}
        </Box>
    )
}

// ── Forecast row helper ──────────────────────────────────────

function ForecastRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
                {value}
            </Typography>
        </Box>
    )
}

// ── Stripe sync + invoice link ──────────────────────────────

function BillingSyncRow({
    usage,
    fmt,
    goToInvoices,
}: {
    usage: UsageData
    fmt: (cents: number) => string
    goToInvoices: () => void
}) {
    const { reported, latestInvoice } = usage
    return (
        <Box sx={{ mt: 1.5, px: 1.5 }}>
            {usage.managedAccounts && (
                <>
                    <ForecastRow
                        label="Own usage"
                        value={`${formatNumber(usage.ownEvents ?? 0)} events`}
                    />
                    <ForecastRow
                        label={`Managed accounts (${formatNumber(usage.managedAccounts.count)})`}
                        value={`${formatNumber(usage.managedAccounts.events)} events`}
                    />
                </>
            )}
            <ForecastRow
                label={`Reported to ${usage.billingProvider === 'stripe' ? 'Stripe' : 'the provider'}`}
                value={`${formatNumber(reported.events)} of ${formatNumber(usage.eventsUsed)} events`}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {usage.billingProvider === 'stripe'
                    ? `Usage is sent hourly${reported.lastReportedAt ? `, last at ${new Date(`${reported.lastReportedAt.replace(' ', 'T')}Z`).toLocaleString()}` : ''}.`
                    : 'Usage for this provider is invoiced at the end of the month.'}
                {reported.lastError && ' The last send failed and is being retried.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {latestInvoice && (
                    <Button
                        size="small"
                        startIcon={<OpenInNew />}
                        href={latestInvoice.url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Latest invoice ({fmt(latestInvoice.amountCents)}, {latestInvoice.status})
                    </Button>
                )}
                <Button size="small" onClick={goToInvoices}>
                    All invoices
                </Button>
            </Box>
        </Box>
    )
}

// ── Free allowance notice (80 % and used up) ──

function FreeAllowanceNotice({ usage }: { usage: UsageData }) {
    const { eventsUsed, freeEventsIncluded: allowance, periodEnd } = usage
    if (allowance <= 0 || eventsUsed < allowance * 0.8) {
        return null
    }
    const resets = new Date(Date.parse(periodEnd) + 1000).toLocaleDateString(undefined, {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'long',
    })
    return eventsUsed >= allowance ? (
        <Alert severity="error" sx={{ mb: 2 }}>
            This month&apos;s {formatNumber(allowance)} free events are used. New events are
            rejected (HTTP 429) until {resets} (UTC); purchases, leads and sign-ups keep being
            accepted for another 10%. Activate pay-as-you-go to keep collecting everything; the
            first {formatNumber(allowance)} events stay free.
        </Alert>
    ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
            {Math.floor((eventsUsed / allowance) * 100)}% of this month&apos;s{' '}
            {formatNumber(allowance)} free events used. The allowance resets on {resets} (UTC).
        </Alert>
    )
}

// ── Plan limits (what the pricing page promises, as enforced) ──

function PlanLimitsCard({ limits }: { limits: UsageData['limits'] }) {
    const n = (v: number | null, unit = '') =>
        v === null ? 'Unlimited' : `${formatNumber(v)}${unit}`
    const rows: [string, string][] = [
        [
            'Projects',
            limits.projects.max === null
                ? `${formatNumber(limits.projects.used)} (unlimited)`
                : `${formatNumber(limits.projects.used)} of ${formatNumber(limits.projects.max)}`,
        ],
        ['Members', n(limits.members.max)],
        [
            'Data retention',
            limits.retentionDays.max > limits.retentionDays.default
                ? `${limits.retentionDays.default} days by default, up to ${formatNumber(limits.retentionDays.max)} days`
                : `${limits.retentionDays.default} days`,
        ],
        ['Monthly event cap', limits.monthlyEventCap === null ? 'None' : n(limits.monthlyEventCap)],
        ['Daily event cap', limits.dailyEventCap === null ? 'None' : n(limits.dailyEventCap)],
        ['Burst limit', `${formatNumber(limits.burstEventsPerMinute)} events per minute`],
    ]
    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Plan limits
                </Typography>
                {rows.map(([label, value]) => (
                    <ForecastRow key={label} label={label} value={value} />
                ))}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                >
                    The burst limit stops runaway loops and floods; a normal site never reaches it.
                    Requests over it get HTTP 429 with Retry-After.
                </Typography>
            </CardContent>
        </Card>
    )
}

// ── Main tab component ──────────────────────────────────────

interface BillingUsageTabProps {
    subscription: SubscriptionData | null | undefined
    usage: UsageData | undefined
    /** Switch to the invoices tab (index 1) */
    goToInvoices: () => void
}

export function BillingUsageTab({ subscription, usage, goToInvoices }: BillingUsageTabProps) {
    const [createCheckout, { isLoading: checkoutLoading }] = useCreateCheckoutMutation()
    const [cancelSubscription] = useCancelSubscriptionMutation()
    const [toggleAiAddon, { isLoading: togglingAddon }] = useToggleAiAddonMutation()
    const [createPortal] = useCreatePortalSessionMutation()
    const [verifyPayment] = useVerifyPaymentMutation()

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
    const [cancelReason, setCancelReason] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    const currentPlan = (subscription?.plan ?? usage?.plan ?? 'free') as TenantPlan
    const planDef = resolvePlan(currentPlan)
    const isPayg = isBillablePlan(currentPlan)
    const aiAddonEnabled = !!(subscription?.aiAddon || (usage?.aiAddon ?? 0))

    const eventsUsed = usage?.eventsUsed ?? subscription?.eventsUsed ?? 0
    const freeIncluded = usage?.freeEventsIncluded ?? planDef.freeEventsIncluded

    // Currency from API (falls back to USD for safety)
    const currency: SupportedCurrency = usage?.currency ?? 'USD'
    const fmt = (cents: number) => formatCurrency(cents, currency)

    const estimatedEventCost = usage?.estimatedCostCents ?? calculateEventCost(eventsUsed, currency)
    const totalEstimatedCost =
        estimatedEventCost + (usage?.aiRetainerCents ?? 0) + (usage?.aiRunCostCents ?? 0)

    // Handle Stripe checkout redirect
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get('success') === 'true') {
            window.history.replaceState({}, '', window.location.pathname)
            setSuccess('Pay-as-you-go billing activated!')
        }
        if (params.get('cancelled') === 'true') {
            window.history.replaceState({}, '', window.location.pathname)
        }
    }, [])

    // ── Handlers ──────────────────────────────────────────

    const handleActivatePayg = async () => {
        setError('')
        try {
            const result = await createCheckout({
                successUrl: `${dashboardUrl(window.location.origin, DASHBOARD_LINKS.billing)}?success=true`,
                cancelUrl: `${dashboardUrl(window.location.origin, DASHBOARD_LINKS.billing)}?cancelled=true`,
            }).unwrap()

            if (result.provider === 'razorpay' && result.subscriptionId && result.razorpayKeyId) {
                await openRazorpayCheckout(result.razorpayKeyId, result.subscriptionId)
            } else if (result.url) {
                window.location.href = result.url
            } else {
                setError('Checkout session could not be created. Please try again.')
            }
        } catch (err: unknown) {
            const msg =
                err && typeof err === 'object' && 'data' in err
                    ? String(
                          (err as { data?: { message?: string } }).data?.message ??
                              'Something went wrong',
                      )
                    : 'Something went wrong'
            setError(msg)
        }
    }

    const openRazorpayCheckout = (keyId: string, subscriptionId: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const loadAndOpen = () => {
                const options = {
                    key: keyId,
                    subscription_id: subscriptionId,
                    name: 'YOM',
                    description: 'Pay-as-you-go subscription',
                    handler: async (response: {
                        razorpay_payment_id: string
                        razorpay_subscription_id: string
                        razorpay_signature: string
                    }) => {
                        try {
                            await verifyPayment({
                                razorpayPaymentId: response.razorpay_payment_id,
                                razorpaySubscriptionId: response.razorpay_subscription_id,
                                razorpaySignature: response.razorpay_signature,
                            }).unwrap()
                            setSuccess('Pay-as-you-go billing activated!')
                        } catch {
                            setError('Payment verification failed. Please contact support.')
                        }
                        resolve()
                    },
                    modal: {
                        ondismiss: () => {
                            resolve()
                        },
                    },
                    theme: { color: '#1976d2' },
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const rzp = new (window as any).Razorpay(options)
                rzp.on('payment.failed', (resp: { error?: { description?: string } }) => {
                    setError(resp?.error?.description ?? 'Payment failed')
                    reject(new Error(resp?.error?.description ?? 'Payment failed'))
                })
                rzp.open()
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).Razorpay) {
                loadAndOpen()
            } else {
                const script = document.createElement('script')
                script.src = 'https://checkout.razorpay.com/v1/checkout.js'
                script.onload = loadAndOpen
                script.onerror = () => {
                    setError('Failed to load Razorpay checkout')
                    reject(new Error('Failed to load Razorpay checkout'))
                }
                document.body.appendChild(script)
            }
        })
    }

    const handleCancel = async () => {
        setError('')
        try {
            await cancelSubscription({
                atPeriodEnd: true,
                reason: cancelReason || undefined,
            }).unwrap()
            setCancelDialogOpen(false)
            setCancelReason('')
            setSuccess('Subscription will be cancelled at end of billing period')
        } catch {
            setError('Failed to cancel subscription')
        }
    }

    const handleToggleAiAddon = async () => {
        setError('')
        if (!isPayg) {
            setError('Activate pay-as-you-go billing first to enable AI agents')
            return
        }
        try {
            const result = await toggleAiAddon({ enabled: !aiAddonEnabled }).unwrap()
            setSuccess(result.aiAddon ? 'AI Add-on enabled' : 'AI Add-on disabled')
        } catch {
            setError('Failed to toggle AI Add-on')
        }
    }

    const handlePortal = async () => {
        try {
            const result = await createPortal({
                returnUrl: window.location.href,
            }).unwrap()

            if (result.inApp) {
                // Razorpay — no hosted portal, switch to invoices tab
                goToInvoices()
                return
            }
            if (result.url) {
                window.location.href = result.url
            }
        } catch {
            setError('Failed to open billing portal')
        }
    }

    // ── Render ──────────────────────────────────────────

    return (
        <>
            {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {!isPayg && usage && <FreeAllowanceNotice usage={usage} />}

            <Grid container spacing={3}>
                {/* Current Plan Card */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary">
                                Current Plan
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                                {planDef.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {planDef.description}
                            </Typography>

                            <Chip
                                label={subscription?.status ?? (isPayg ? 'active' : 'free')}
                                color={
                                    subscription?.status === 'active' || isPayg
                                        ? 'success'
                                        : 'default'
                                }
                                size="small"
                                sx={{ textTransform: 'capitalize' }}
                            />

                            {subscription?.currentPeriodEnd && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    {subscription.cancelAtPeriodEnd ? 'Cancels' : 'Renews'}{' '}
                                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                </Typography>
                            )}

                            <Divider sx={{ my: 2 }} />

                            {!isPayg ? (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={
                                        checkoutLoading ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <Upgrade />
                                        )
                                    }
                                    disabled={checkoutLoading}
                                    onClick={handleActivatePayg}
                                >
                                    Activate Pay-as-you-go
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {usage?.billingProvider === 'stripe' && (
                                        <Button
                                            size="small"
                                            startIcon={<OpenInNew />}
                                            onClick={handlePortal}
                                        >
                                            Billing Portal
                                        </Button>
                                    )}
                                    {!subscription?.cancelAtPeriodEnd && (
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<Cancel />}
                                            onClick={() => setCancelDialogOpen(true)}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            {/* AI Add-on Toggle */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle2">
                                        <SmartToy
                                            sx={{
                                                fontSize: 16,
                                                mr: 0.5,
                                                verticalAlign: 'text-bottom',
                                            }}
                                        />
                                        AI Insights Add-on
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {fmt(AI_PRICING.retainer[currency])}/mo —{' '}
                                        {AI_PRICING.includedRuns} runs included, graduated pricing
                                        after
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={aiAddonEnabled}
                                            onChange={handleToggleAiAddon}
                                            disabled={togglingAddon || !isPayg}
                                            size="small"
                                        />
                                    }
                                    label=""
                                />
                            </Box>
                            {!isPayg && (
                                <Typography variant="caption" color="text.secondary">
                                    Activate pay-as-you-go to enable AI Add-on
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Usage Dashboard */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                }}
                            >
                                <Typography variant="h6">
                                    <TrendingUp
                                        sx={{
                                            fontSize: 20,
                                            mr: 1,
                                            verticalAlign: 'text-bottom',
                                        }}
                                    />
                                    Usage This Period
                                </Typography>
                                {isPayg && (
                                    <Chip
                                        label={`Est. cost: ${fmt(totalEstimatedCost)}`}
                                        color={totalEstimatedCost > 5000 ? 'warning' : 'default'}
                                        variant="outlined"
                                    />
                                )}
                            </Box>

                            <UsageMeter
                                label={isPayg ? 'Free allowance used' : 'Events this month'}
                                used={isPayg ? Math.min(eventsUsed, freeIncluded) : eventsUsed}
                                limit={freeIncluded}
                                subText={
                                    isPayg
                                        ? `${formatNumber(eventsUsed)} events this month (exact). The first ${formatNumber(freeIncluded)} are free; ${formatNumber(usage?.billableEvents ?? Math.max(0, eventsUsed - freeIncluded))} billable.`
                                        : `${formatNumber(usage?.freeEventsRemaining ?? Math.max(0, freeIncluded - eventsUsed))} left this month. No daily cap; the allowance resets on the 1st (UTC).`
                                }
                            />

                            {/* AI usage meter (shown only when addon enabled) */}
                            {aiAddonEnabled && (
                                <UsageMeter
                                    label="AI Agent Runs"
                                    used={usage?.aiCreditsUsed ?? 0}
                                    limit={Infinity}
                                    subText={`${AI_PRICING.includedRuns} runs included with retainer. Graduated pricing beyond.`}
                                />
                            )}

                            {/* Cost Breakdown for PAYG */}
                            {isPayg && eventsUsed > 0 && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Cost Breakdown
                                    </Typography>

                                    {/* Event tiers */}
                                    {EVENT_TIERS.map((tier, i) => {
                                        const prevUpTo = i > 0 ? EVENT_TIERS[i - 1].upTo : 0
                                        const tierCapacity = tier.upTo - prevUpTo
                                        const eventsInTier = Math.min(
                                            Math.max(0, eventsUsed - prevUpTo),
                                            tierCapacity,
                                        )
                                        if (eventsInTier <= 0) {
                                            return null
                                        }
                                        const tierCost =
                                            (eventsInTier / 1000) * tierPricePerK(tier, currency)

                                        return (
                                            <Box
                                                key={tier.label}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    py: 0.5,
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    {tier.label} ({formatNumber(eventsInTier)}{' '}
                                                    events @{' '}
                                                    {tierPricePerK(tier, currency) === 0
                                                        ? 'Free'
                                                        : `${fmt(tierPricePerK(tier, currency))}/1K`}
                                                    )
                                                </Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {fmt(Math.round(tierCost))}
                                                </Typography>
                                            </Box>
                                        )
                                    })}

                                    {/* AI costs */}
                                    {aiAddonEnabled && (
                                        <>
                                            <Divider sx={{ my: 1 }} />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    py: 0.5,
                                                }}
                                            >
                                                <Typography variant="body2">AI Retainer</Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {fmt(usage?.aiRetainerCents ?? 0)}
                                                </Typography>
                                            </Box>
                                            {(usage?.aiRunCostCents ?? 0) > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        py: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        AI Runs ({usage?.aiCreditsUsed ?? 0} runs)
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {fmt(usage?.aiRunCostCents ?? 0)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </>
                                    )}

                                    <Divider sx={{ my: 1 }} />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography variant="subtitle2">Total</Typography>
                                        <Typography variant="subtitle2" color="primary.main">
                                            {fmt(totalEstimatedCost)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Monthly Forecast */}
                            {isPayg && usage && usage.daysElapsed > 0 && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: 'info.50',
                                        border: '1px solid',
                                        borderColor: 'info.200',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        <CalendarMonth
                                            sx={{
                                                fontSize: 16,
                                                mr: 0.5,
                                                verticalAlign: 'text-bottom',
                                            }}
                                        />
                                        Monthly Forecast
                                    </Typography>
                                    <ForecastRow
                                        label="Daily average"
                                        value={`${formatNumber(usage.dailyAverage)} events/day`}
                                    />
                                    <ForecastRow
                                        label="Period progress"
                                        value={`Day ${usage.daysElapsed} of ${usage.daysInPeriod}`}
                                    />
                                    <ForecastRow
                                        label="Projected events"
                                        value={`~${formatNumber(usage.forecastEventCount)}`}
                                    />
                                    {aiAddonEnabled && usage.forecastAiRuns > 0 && (
                                        <ForecastRow
                                            label="Projected AI runs"
                                            value={`~${usage.forecastAiRuns}`}
                                        />
                                    )}
                                    <Divider sx={{ my: 1 }} />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography variant="subtitle2">
                                            Projected end-of-month cost
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            color={
                                                usage.forecastCostCents > 10000
                                                    ? 'error.main'
                                                    : 'primary.main'
                                            }
                                        >
                                            ~{fmt(usage.forecastCostCents)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, display: 'block' }}
                                    >
                                        Based on your current daily run rate, priced like the public
                                        pricing calculator. Actual cost may vary.
                                        {usage.localEstimate &&
                                            ` About ${formatCurrency(usage.localEstimate.forecastCents, usage.localEstimate.currency)} (estimate only; billed in ${currency}).`}
                                    </Typography>
                                </Box>
                            )}

                            {/* Billing Provider */}
                            {usage && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        bgcolor: 'grey.50',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Payment sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Billing via{' '}
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{ textTransform: 'capitalize' }}
                                        >
                                            {usage.billingProvider}
                                        </Typography>{' '}
                                        ({usage.country}) · charged in {usage.currency}
                                    </Typography>
                                </Box>
                            )}
                            {usage && isPayg && (
                                <BillingSyncRow
                                    usage={usage}
                                    fmt={fmt}
                                    goToInvoices={goToInvoices}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {usage?.limits && <PlanLimitsCard limits={usage.limits} />}

            {/* Pricing Tiers Reference Table */}
            <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
                Event Pricing — Graduated Tiers
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each tier&apos;s price applies only to events within that range — like tax brackets.
                The more you use, the cheaper each additional event.
            </Typography>
            <Card>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Tier</TableCell>
                            <TableCell>Volume Range</TableCell>
                            <TableCell>Price per 1,000 events</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {EVENT_TIERS.map((tier, i) => {
                            const prevUpTo = i > 0 ? EVENT_TIERS[i - 1].upTo : 0
                            const isActive =
                                eventsUsed > prevUpTo &&
                                (i === EVENT_TIERS.length - 1 || eventsUsed <= tier.upTo)

                            return (
                                <TableRow
                                    key={tier.label}
                                    sx={{
                                        bgcolor: isActive ? 'primary.50' : undefined,
                                        '& > td': isActive ? { fontWeight: 600 } : undefined,
                                    }}
                                >
                                    <TableCell>{tier.label}</TableCell>
                                    <TableCell>
                                        {formatNumber(prevUpTo)} —{' '}
                                        {isFinite(tier.upTo)
                                            ? formatNumber(tier.upTo)
                                            : 'Unlimited'}
                                    </TableCell>
                                    <TableCell>
                                        {tierPricePerK(tier, currency) === 0 ? (
                                            <Chip label="Free" size="small" color="success" />
                                        ) : (
                                            fmt(tierPricePerK(tier, currency))
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {isActive && (
                                            <CheckCircle
                                                sx={{ fontSize: 16, color: 'primary.main' }}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </Card>

            {/* Cancel Dialog */}
            <Dialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Cancel Subscription</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Your pay-as-you-go subscription will remain active until the end of the
                        current billing period. After that, you&apos;ll revert to the Free plan (
                        {formatNumber(freeIncluded)} events/month, limited features).
                    </Typography>
                    <TextField
                        label="Reason (optional)"
                        value={cancelReason}
                        onChange={e => setCancelReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>Keep Plan</Button>
                    <Button variant="contained" color="error" onClick={handleCancel}>
                        Cancel Subscription
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
